/**
 * The shell backgrounds a user can choose from the topbar, and where each one
 * lives. One table, read by both the picker (for its thumbnails) and the shell
 * (for the image itself), so the two can never disagree about a URL.
 *
 * The images are static SVGs in `public/backgrounds/`, drawn from the
 * product's own world — a CGM trace, the round sensor, the Najd dunes — and
 * deliberately NOT animated: the GIFs these replace moved under text all day,
 * and WCAG 2.2.2 gives a moving background no way to be paused from CSS.
 */

import type { Background } from './providers/appearance';

export interface BackgroundOption {
  id: Background;
  /** `null` for the plain ground. */
  url: string | null;
  labelKey:
    | 'background.none'
    | 'background.glucoseTrace'
    | 'background.sensorRings'
    | 'background.dunes'
    | 'background.contours'
    | 'background.aurora'
    | 'background.nightSky';
}

export const BACKGROUND_OPTIONS: readonly BackgroundOption[] = [
  { id: 'none', url: null, labelKey: 'background.none' },
  { id: 'glucose-trace', url: '/backgrounds/glucose-trace.svg', labelKey: 'background.glucoseTrace' },
  { id: 'sensor-rings', url: '/backgrounds/sensor-rings.svg', labelKey: 'background.sensorRings' },
  { id: 'dunes', url: '/backgrounds/dunes.svg', labelKey: 'background.dunes' },
  { id: 'contours', url: '/backgrounds/contours.svg', labelKey: 'background.contours' },
  { id: 'aurora', url: '/backgrounds/aurora.svg', labelKey: 'background.aurora' },
  { id: 'night-sky', url: '/backgrounds/night-sky.svg', labelKey: 'background.nightSky' },
];

export function backgroundUrl(id: Background): string | null {
  return BACKGROUND_OPTIONS.find((option) => option.id === id)?.url ?? null;
}
